import React, { useContext, useEffect, useState } from "react";
import Store from "../../Store/Store";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { GoogleLogin } from "@react-oauth/google";
import configuration from "../../configuration.json";
import Typography from "@mui/material/Typography";
import LoginScreenBG from "../../assets/img/LoginScreenBG.svg";

import {
  exchangeUserToken,
  identityConfig,
} from "../../clients/identityClient";
import { getUserProfile } from "../../clients/authClient";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga";
import LogoStamp from "../../assets/img/SpecLogoStamp.png";

import Footer from "../../components/Footer";
import { Spinner } from "reactstrap";
import utilFunctions from "../../utilities/utilFunctions";
import { Box, Link, Button, Avatar, useMediaQuery } from "@mui/material";
import AppleSignInButton from "react-apple-signin-auth";

const Login = ({ handleLogInSuccess }) => {
  const store = useContext(Store);
  const [loading, toggleLoading] = useState(false);
  const [fetchingComplete, toggleFetchingComplete] = useState(false);
  const [error, toggleError] = useState(false);
  let navigate = useNavigate();
  const useMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
    const fetchUserData = async () => {
      toggleLoading(true);
      const user = await getUserProfile();
      const tokenData = utilFunctions.parseJwt(identityConfig.accessToken);
      toggleLoading(false);
      handleLogInSuccess(user.profile, tokenData.sub, true);
      toggleFetchingComplete(true);
    };
    try {
      toggleError(false);
      if (store.authenticated) {
        fetchUserData();
      } else {
        toggleLoading(false);
        toggleFetchingComplete(true);
      }
    } catch (err) {
      console.log(err);
      toggleLoading(false);
      toggleError(true);
      toggleFetchingComplete(true);
    }
  }, []);

  const handleSocialLoginSuccess = async (resp, socialProvider) => {
    try {
      toggleError(false);
      const token =
        socialProvider === "google"
          ? resp.credential
          : socialProvider === "apple"
          ? resp?.authorization?.id_token
          : resp.accessToken;
      const exchangeResult = await exchangeUserToken(token, socialProvider);
      localStorage.setItem("at", exchangeResult.access_token);
      const tokenData = utilFunctions.parseJwt(exchangeResult.access_token);
      const user = await getUserProfile();
      // hacky way for us to reload for split io sdk user refresh
      // if we don't have a profile we don't want to refresh, we want to go to the profile creation screen
      let skipReload = user?.profile === null ? true : false;
      handleLogInSuccess(user.profile, tokenData.sub, skipReload);
    } catch (err) {
      console.log(err);
      toggleError(true);
    }
  };

  const handleGoogleLoginError = (resp) => {
    console.log(resp);
  };

  if (store.authenticated && fetchingComplete) {
    if (store.user && store.user.firstName) {
      navigate("/establishments");
    } else {
      navigate("/create-profile");
    }
  }

  return (
    <>
      {/* Top Header */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "60px",
          backgroundColor: "#000000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingX: { xs: 2, md: 4 },
          zIndex: 1000,
        }}
      >
        <Link
          href="https://www.specapp.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Avatar
            alt="Spec Logo"
            src={LogoStamp}
            sx={{
              height: "32px",
              width: "32px",
              marginRight: "8px",
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: "#fff",
              fontSize: "32px",
            }}
          >
            Spec
          </Typography>
        </Link>
      </Box>

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{
          background: "#000000",
          minHeight: "100%",
          padding: 6,
          paddingTop: "80px", // Add padding to account for fixed header
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background SVG */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "1400px", sm: "1400px", md: "1400px", lg: "1600px" },
            height: { xs: "1190px", sm: "1190px", md: "1190px", lg: "1360px" },
            opacity: 0.8,
            pointerEvents: "none",
            zIndex: 0,
            "& svg": {
              width: "100%",
              height: "100%",
            },
          }}
        >
          <img
            src={LoginScreenBG}
            alt=""
            style={{ width: "100%", height: "100%" }}
          />
        </Box>

        <Grid
          container
          sx={{
            maxWidth: "2440px",
            margin: "auto",
            display: "flex",
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            // md={4}
            // lg={4}
            // xl={4}
            sx={{
              zIndex: 1,
              marginTop: "auto",
              marginBottom: "auto",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Grid
              item
              sx={{
                marginBottom: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    color: "#fff",
                    fontSize: { xs: 24, sm: 24, md: "60px" },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Welcome to Spec
                </Typography>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  color: "#fff",
                  fontSize: { xs: 20, sm: 20, md: 24 },
                  textAlign: "center",
                }}
              >
                Do more in less time
              </Typography>
            </Grid>
            <Grid
              container
              direction="column"
              sx={{
                display: "flex",
                marginTop: "auto",
                marginBottom: "auto",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#fff",
                  textAlign: "left",
                  marginBottom: 6,
                }}
              >
                Need a Spec account?{" "}
                <Link href="/" color="inherit" sx={{ color: "#fff" }}>
                  Create your account
                </Link>
              </Typography>

              <Grid item>
                {loading ? (
                  <Spinner color="primary" size="lg" />
                ) : (
                  <Card
                    sx={{
                      maxWidth: { xs: "300px", md: "450px" },
                      maxHeight: "500px",
                      marginBottom: 4,
                      borderRadius: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      border: "4px solid #fff",
                      color: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        marginTop: 8,
                        textAlign: "center",
                      }}
                      variant="body1"
                    >
                      Already have an account?
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ textAlign: "center", marginTop: 4 }}
                    >
                      Log In
                    </Typography>
                    {error && (
                      <Typography
                        style={{
                          margin: "8px 4px 8px 4px",
                          color: "red",
                          textAlign: "center",
                        }}
                        variant="body1"
                      >
                        An error ocurred trying to log you in, please try again.
                      </Typography>
                    )}
                    <CardContent>
                      <Box
                        style={{
                          marginLeft: "auto",
                          marginRight: "auto",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <FacebookLogin
                          appId="465358737667031"
                          fields="name,email"
                          callback={(resp) =>
                            handleSocialLoginSuccess(resp, "facebook")
                          }
                          render={(renderProps) => (
                            <Button
                              variant="outlined"
                              onClick={renderProps.onClick}
                              sx={{
                                width: "300px",
                                height: "48px",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                                paddingTop: 3,
                                paddingBottom: 3,
                                borderColor: "#000",
                                "&:hover": {
                                  backgroundColor: "#c6c6c6",
                                },
                              }}
                            >
                              <svg
                                fill="#4a90e2"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 16 16"
                                width="28px"
                                height="28px"
                              >
                                <path d="M 7.5 1 C 3.9160714 1 1 3.9160714 1 7.5 C 1 11.083929 3.9160714 14 7.5 14 C 11.083929 14 14 11.083929 14 7.5 C 14 3.9160714 11.083929 1 7.5 1 z M 7.5 2 C 10.543488 2 13 4.4565116 13 7.5 C 13 10.266333 10.967571 12.541024 8.3125 12.933594 L 8.3125 9.0898438 L 9.8652344 9.0898438 L 10.109375 7.5136719 L 8.3125 7.5136719 L 8.3125 6.6503906 C 8.3125 5.9953906 8.5256719 5.4140625 9.1386719 5.4140625 L 10.123047 5.4140625 L 10.123047 4.0371094 C 9.9500469 4.0141094 9.5845781 3.9628906 8.8925781 3.9628906 C 7.4485781 3.9628906 6.6015625 4.7258906 6.6015625 6.4628906 L 6.6015625 7.5117188 L 5.1171875 7.5117188 L 5.1171875 9.0898438 L 6.6035156 9.0898438 L 6.6035156 12.919922 C 3.9897868 12.492118 2 10.237066 2 7.5 C 2 4.4565116 4.4565116 2 7.5 2 z" />
                              </svg>
                              <Typography
                                variant="h6"
                                sx={{
                                  marginLeft: 2,
                                  color: "#000",
                                  textTransform: "none",
                                }}
                              >
                                Facebook
                              </Typography>
                            </Button>
                          )}
                        />
                      </Box>
                      <Box
                        sx={{
                          marginLeft: "auto",
                          marginRight: "auto",
                          marginTop: "1em",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <GoogleLogin
                          useOneTap={false}
                          auto_select={false}
                          onSuccess={(resp) => {
                            handleSocialLoginSuccess(resp, "google");
                          }}
                          onFailure={handleGoogleLoginError}
                          cookiePolicy={"single_host_origin"}
                          width={useMobile ? "255" : "300"}
                          size="large"
                          shape="rectangular"
                        />
                      </Box>
                      <Box
                        sx={{
                          marginLeft: "auto",
                          marginRight: "auto",
                          marginTop: "1em",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <AppleSignInButton
                          authOptions={{
                            clientId: configuration.appleClientId,
                            scope: "email name",
                            redirectURI: configuration.appleRedirectUri,
                            state: "state",
                            nonce: "nonce",
                            usePopup: true,
                          }}
                          uiType="light"
                          className="apple-auth-btn"
                          buttonExtraChildren="Continue with Apple"
                          onSuccess={(response) =>
                            handleSocialLoginSuccess(response, "apple")
                          }
                          onError={(error) => console.error(error)}
                          skipScript={false}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>
              <Grid item xs={12} sx={{ marginTop: 10 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#fff",
                    textAlign: "center",
                    maxWidth: { xs: "300px", md: "450px" },
                  }}
                >
                  Account admins gain access to our dynamic dashboard feature
                  and are able to build out a digital home for your
                  establishment{"'"}s food and beverage content.
                </Typography>
              </Grid>
              <Grid item sx={{ marginTop: 6 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#fff",
                    textAlign: "center",
                    maxWidth: { xs: "300px", md: "450px" },
                  }}
                >
                  Staff are able to download and view your content via our
                  mobile app:
                </Typography>
              </Grid>
              <Grid
                item
                sx={{
                  marginTop: 6,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "row",
                }}
              >
                <Grid
                  container
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "50px",
                    marginTop: 4,
                    paddingLeft: 2,
                  }}
                >
                  <Box>
                    <Link
                      href="https://apps.apple.com/us/app/spec-pro/id1454755076?itsct=apps_box_badge&amp;itscg=30200"
                      sx={{
                        width: "125px",
                      }}
                    >
                      <img
                        src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1552176000&h=64eba89b8337a122e132b26421e24f4f"
                        alt="Download on the App Store"
                        style={{
                          width: "125px",
                        }}
                      />
                    </Link>
                  </Box>
                  <Box>
                    <Link href="https://play.google.com/store/apps/details?id=com.spec.pro&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1">
                      <img
                        alt="Get it on Google Play"
                        src="https://mcusercontent.com/54c65f604256d6be9651d3858/images/199492b4-3f5c-ba0c-e1a2-6e304c90b1d1.png"
                        style={{ width: "160px" }}
                      />
                    </Link>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Footer />
    </>
  );
};

export default Login;
